import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { COLORS } from './constants';
import { GroupRow, DashboardNavState, CustomDateRange, WeeklyDataPoint, FeatureUsagePoint, TagOption } from './types';
import { DashboardApiService } from '../../../../services/dashboard.service';
import { StatCard } from '../../../../components/groups-teams-dashboard/stat-card';
import { BreadcrumbNav } from '../../../../components/groups-teams-dashboard/breadcrumb-nav';
import { FilterBar } from '../../../../components/groups-teams-dashboard/filter-bar';
import { WeeklyBarChart } from '../../../../components/groups-teams-dashboard/weekly-bar-chart';
import { FeatureBarChart } from '../../../../components/groups-teams-dashboard/feature-bar-chart';
import { GroupsTable } from '../../../../components/groups-teams-dashboard/groups-table';
import { DashboardSkeleton } from '../../../../components/groups-teams-dashboard/dashboard-skeleton';
import { exportGroupsCsv } from './export-csv';

interface Level1GroupsProps {
    onNavigate: (nav: Partial<DashboardNavState>) => void;
}

export const Level1Groups: React.FC<Level1GroupsProps> = ({ onNavigate }) => {
    const [timePeriod, setTimePeriod] = useState('7d');
    const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({ startDate: null, endDate: null });
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [appliedTags, setAppliedTags] = useState<string[]>([]);
    const [selectedUserTags, setSelectedUserTags] = useState<string[]>([]);
    const [appliedUserTags, setAppliedUserTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    // Data state
    const [groups, setGroups] = useState<GroupRow[]>([]);
    const [summary, setSummary] = useState({ groups: 0, teams: 0, totalUsers: 0, active: 0, inactive: 0 });
    const [weeklyActive, setWeeklyActive] = useState<WeeklyDataPoint[]>([]);
    const [featureUsage, setFeatureUsage] = useState<FeatureUsagePoint[]>([]);
    const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
    const [userTagOptions, setUserTagOptions] = useState<TagOption[]>([]);

    // Initial load: fetch everything
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const startDate = timePeriod === 'custom' && customDateRange.startDate
                ? customDateRange.startDate.toISOString() : undefined;
            const endDate = timePeriod === 'custom' && customDateRange.endDate
                ? customDateRange.endDate.toISOString() : undefined;

            const [groupsData, weekly, features, tags, uTags] = await Promise.all([
                DashboardApiService.fetchGroups(timePeriod, startDate, endDate, selectedTags),
                DashboardApiService.fetchWeeklyActive('all'),
                DashboardApiService.fetchFeatureUsage(timePeriod, 'all', undefined, startDate, endDate),
                DashboardApiService.fetchTagOptions('team'),
                DashboardApiService.fetchTagOptions('user'),
            ]);

            const allGroups = groupsData.noGroup
                ? [...groupsData.groups, groupsData.noGroup]
                : groupsData.groups;

            setGroups(allGroups);
            setSummary(groupsData.summary);
            setWeeklyActive(weekly);
            setFeatureUsage(features);
            setTagOptions(tags);
            setUserTagOptions(uTags);
        } catch (err) {
            console.error('Failed to fetch dashboard data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Filter change: only re-fetch date/tag-dependent data (skip weeklyActive & tagOptions)
    const fetchFiltered = useCallback(async (
        period: string,
        dateRange: CustomDateRange,
        tags: string[],
        userTags: string[]
    ) => {
        setFilterLoading(true);
        try {
            const startDate = period === 'custom' && dateRange.startDate
                ? dateRange.startDate.toISOString() : undefined;
            const endDate = period === 'custom' && dateRange.endDate
                ? dateRange.endDate.toISOString() : undefined;

            const [groupsData, features] = await Promise.all([
                DashboardApiService.fetchGroups(period, startDate, endDate, tags, userTags),
                DashboardApiService.fetchFeatureUsage(period, 'all', undefined, startDate, endDate),
            ]);

            const allGroups = groupsData.noGroup
                ? [...groupsData.groups, groupsData.noGroup]
                : groupsData.groups;

            setGroups(allGroups);
            setSummary(groupsData.summary);
            setFeatureUsage(features);
        } catch (err) {
            console.error('Failed to fetch filtered data:', err);
        } finally {
            setFilterLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, []); // Load on mount

    const handleApply = () => {
        setAppliedTags([...selectedTags]);
        setAppliedUserTags([...selectedUserTags]);
        fetchFiltered(timePeriod, customDateRange, selectedTags, selectedUserTags);
    };

    const handleClear = () => {
        setTimePeriod('7d');
        setCustomDateRange({ startDate: null, endDate: null });
        setSelectedTags([]);
        setAppliedTags([]);
        setSelectedUserTags([]);
        setAppliedUserTags([]);
        fetchFiltered('7d', { startDate: null, endDate: null }, [], []);
    };

    const handleRemoveAppliedTag = (tag: string) => {
        const updated = selectedTags.filter((t) => t !== tag);
        setSelectedTags(updated);
        setAppliedTags(updated);
        fetchFiltered(timePeriod, customDateRange, updated, appliedUserTags);
    };

    const handleRemoveAppliedUserTag = (tag: string) => {
        const updated = selectedUserTags.filter((t) => t !== tag);
        setSelectedUserTags(updated);
        setAppliedUserTags(updated);
        fetchFiltered(timePeriod, customDateRange, appliedTags, updated);
    };

    const handleGroupClick = (group: GroupRow) => {
        onNavigate({
            level: 2,
            groupId: group.id,
            groupName: group.name,
        });
    };

    if (loading) {
        return <DashboardSkeleton statCards={5} tableColumns={6} tableRows={5} />;
    }

    return (
        <Box sx={{ opacity: filterLoading ? 0.5 : 1, transition: 'opacity 0.2s', pointerEvents: filterLoading ? 'none' : 'auto' }}>
            {/* Breadcrumb */}
            <BreadcrumbNav
                segments={[
                    { label: 'Reports' },
                    { label: 'Groups & Teams Activity' },
                ]}
            />

            {/* Page Header */}
            <Box sx={{ marginBottom: '16px' }}>
                <Box component="h1" sx={{ fontSize: '20px', fontWeight: 600, margin: 0, fontFamily: 'Poppins, sans-serif' }}>
                    Groups & Teams Activity
                </Box>
                <Box sx={{ fontSize: '12px', fontWeight: 500, color: COLORS.grayDark, marginTop: '2px' }}>
                    See how your groups and teams are using POM Safe. Click any group to see its teams.
                </Box>
            </Box>

            {/* Filters */}
            <FilterBar
                timePeriod={timePeriod}
                onTimePeriodChange={setTimePeriod}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
                tagOptions={tagOptions}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                userTagOptions={userTagOptions}
                selectedUserTags={selectedUserTags}
                onUserTagsChange={setSelectedUserTags}
                onApply={handleApply}
                onClear={handleClear}
                onExport={() => exportGroupsCsv(groups)}
                appliedTags={appliedTags}
                onRemoveAppliedTag={handleRemoveAppliedTag}
                appliedUserTags={appliedUserTags}
                onRemoveAppliedUserTag={handleRemoveAppliedUserTag}
            />

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <StatCard value={summary.groups} label="Groups" />
                <StatCard value={summary.teams} label="Teams" />
                <StatCard value={summary.totalUsers} label="Total Users" />
                <StatCard
                    value={summary.active}
                    label="Active"
                    valueColor={COLORS.primary}
                />
                <StatCard
                    value={summary.inactive}
                    label="Inactive"
                    valueColor={COLORS.red}
                />
            </Box>

            {/* Charts */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '14px',
                    marginBottom: '16px',
                }}
            >
                <WeeklyBarChart
                    data={weeklyActive}
                    title="Weekly Active Rate"
                    subtitle="All groups · Last 12 weeks"
                />
                <FeatureBarChart
                    data={featureUsage}
                    title="Feature Usage"
                    subtitle={`All groups · ${timePeriod === '7d' ? 'Last 7 days' : timePeriod}`}
                />
            </Box>

            {/* Groups Table */}
            <GroupsTable
                groups={groups}
                onGroupClick={handleGroupClick}
            />
        </Box>
    );
};
